import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, Eye, EyeOff, Loader2 } from 'lucide-react';
import { api } from '../services/api';

export default function Login() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    senha: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.login(formData.email, formData.senha);

      if (response.success) {
        localStorage.setItem('user', JSON.stringify(response.user));
        localStorage.setItem('token', response.token);
        localStorage.setItem('showWelcome', 'true');
        navigate('/');
      } else {
        setError(response.error || 'Erro ao fazer login');
      }
    } catch (err) {
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-primary-100 via-white to-primary-200 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 flex items-center justify-center px-4 py-12">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-16 -left-10 h-64 w-64 rounded-full bg-primary-400/30 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-primary-300/40 blur-3xl" />
      </div>

      <div className="relative w-full max-w-6xl">
        <div className="grid grid-cols-1 overflow-hidden rounded-3xl border border-primary-200/60 bg-white/90 shadow-[0_40px_80px_-30px_rgba(24,90,188,0.45)] backdrop-blur-xl dark:border-primary-900/40 dark:bg-gray-900/85 lg:grid-cols-[1.1fr_1fr]">
          <div className="relative hidden bg-gradient-to-br from-primary-800 via-primary-700 to-primary-500 p-14 text-primary-50 lg:flex lg:flex-col lg:justify-between">
            <div className="absolute inset-0 opacity-40">
              <div className="absolute -top-20 right-4 h-40 w-40 rounded-full border border-white/40" />
              <div className="absolute bottom-10 left-10 h-48 w-48 rounded-full bg-white/10 blur-2xl" />
              <div className="absolute top-1/3 -left-8 h-32 w-32 rotate-12 border border-white/20" />
            </div>
            <div className="relative space-y-6">
              <span className="inline-flex items-center rounded-full bg-white/10 px-4 py-1 text-sm font-medium tracking-wide">
                Plataforma Inteligente
              </span>
              <h1 className="text-4xl font-black leading-tight xl:text-5xl">
                ControleMax
              </h1>
              <p className="text-lg leading-relaxed text-primary-50/80">
                O seu sistema financeiro, <span className="text-primary-200">sob medida</span>.
              </p>
              <p className="max-w-sm text-sm text-primary-100/75">
                Clareza e foco para a sua gestão financeira.
              </p>
            </div>
          </div>

          <div className="relative flex flex-col justify-center p-8 sm:p-12">
            <div className="mb-8 space-y-4 text-center lg:text-left">
              <div className="space-y-2 lg:hidden">
                <h1 className="text-3xl font-black text-gray-900 dark:text-white">ControleMax</h1>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  O seu sistema financeiro, <span className="text-primary-600 dark:text-primary-300">sob medida</span>.
                </p>
              </div>
              <div className="space-y-2">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white sm:text-3xl">Acesse sua conta</h2>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Bem-vindo de volta ao universo da <span className="font-semibold text-primary-600 dark:text-primary-300">ControleMax</span>.
                </p>
              </div>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg animate-fade-in">
                <p className="text-danger-600 dark:text-danger-400 text-sm font-medium">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  required
                  className="input-field"
                  placeholder="seu@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Senha
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    className="input-field pr-12"
                    placeholder="••••••••"
                    value={formData.senha}
                    onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-primary-600 to-primary-500 text-white py-3 rounded-lg hover:from-primary-700 hover:to-primary-600 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Entrando...</span>
                  </>
                ) : (
                  <>
                    <LogIn className="w-5 h-5" />
                    <span>Entrar</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-8 text-center text-sm text-gray-600 dark:text-gray-400">
          © 2025 ControleMax. Todos os direitos reservados.
        </p>
      </div>
    </div>
  );
}
